import * as THREE from 'three';
import { SPR } from './atlas.js';

/**
 * ===========================================================================
 *  EFFECT DEFINITION TABLE
 * ===========================================================================
 *
 * Everything `world.vfx.emit(name, x,y,z, opts)` can spawn is described here
 * as DATA, so another engineer can add an effect without touching a line of
 * VFX code: copy a def, change the numbers, call `emit('my_thing', ...)`.
 *
 * A def is `{ layers: [...] }`. Each layer has a kind `k`:
 *
 *   k:'p'       particles      the workhorse
 *   k:'ring'    ground shockwave (terrain-conformed, expands over `life`)
 *   k:'decal'   ground decal from the decal atlas
 *   k:'beam'    stretched quad between two points
 *   k:'light'   a pooled point light flash
 *   k:'impulse' world.pipeline.impulse(kind, amount)
 *   k:'shake'   world.camrig.shake(amp, freq, dur)
 *
 * Numeric fields accept a scalar or a `[min,max]` pair which is sampled with
 * the VFX module's private deterministic RNG. Every field is optional.
 *
 * Particle layer fields
 *   n         count (scaled by opts.power and the quality density)
 *   pool      'add' (default) or 'alpha'
 *   sprite    atlas cell index, or an array to pick from
 *   dir       'sphere'|'hemi'|'cone'|'disc'|'up'|'ground'|'back'|'along'
 *   spread    cone half-angle in radians
 *   speed     initial speed (m/s)
 *   offset    random spawn offset radius
 *   yoff      vertical spawn offset
 *   life, size0, size1, drag, grav, spin, spinRate, stretch, turb
 *   align     0 billboard, 1 screen-velocity stretch, 2 ground-flat, 3 axial
 *   fadeIn    fraction of life spent fading in
 *   curve     fade-out exponent (1 linear, 3 snappy)
 *   col/col2  hex colours at t=0 / t=1 (element colour used when omitted)
 *   alpha, alpha2, bright
 *   delay     birth delay
 */

const _c = new THREE.Color();
/** sRGB hex -> linear RGB triple, which is what the HDR target wants. */
export function lin(hex) {
  _c.setHex(hex).convertSRGBToLinear();
  return [_c.r, _c.g, _c.b];
}

/* ------------------------------------------------------------- elements -- */
/**
 * Element look-up. `world.vfx.element(name)` returns one of these; every
 * public call takes `opts.element` and routes colour, sprite, light and the
 * secondary particle behaviour through it.
 */
export const ELEMENTS = {
  none: {
    name: 'none',
    hot: lin(0xfffdf2), core: lin(0xffe9a8), tail: lin(0xff9a34),
    trailHot: lin(0xfffdf4), trailTail: lin(0xff8c22),
    spark: SPR.GLOW, body: SPR.FLAME, motes: SPR.EMBER,
    light: 0xffdca0, lightI: 10, lightR: 10,
    grav: -13, drag: 5.5, turb: 0,
  },
  fire: {
    name: 'fire',
    hot: lin(0xfff5d0), core: lin(0xffa733), tail: lin(0xc22a08),
    trailHot: lin(0xffe9b0), trailTail: lin(0xe83408),
    spark: SPR.EMBER, body: SPR.FLAME, motes: SPR.EMBER,
    light: 0xff7a22, lightI: 12, lightR: 12,
    grav: 3.2, drag: 2.4, turb: 0.55,
  },
  ice: {
    name: 'ice',
    hot: lin(0xf2fdff), core: lin(0x9fe4ff), tail: lin(0x2f7fc8),
    trailHot: lin(0xeafbff), trailTail: lin(0x2f8ce8),
    spark: SPR.SHARD, body: SPR.SHARD, motes: SPR.GLOW,
    light: 0x7fd8ff, lightI: 9, lightR: 11,
    grav: -7, drag: 3.0, turb: 0.15,
  },
  lightning: {
    name: 'lightning',
    hot: lin(0xffffff), core: lin(0xc9e4ff), tail: lin(0x6a55ff),
    trailHot: lin(0xffffff), trailTail: lin(0x7a5cff),
    spark: SPR.BOLT, body: SPR.BOLT, motes: SPR.GLOW,
    light: 0xa9c8ff, lightI: 20, lightR: 15,
    grav: -2, drag: 7.5, turb: 1.4,
  },
  wind: {
    name: 'wind',
    hot: lin(0xf4fff6), core: lin(0xb7f0cf), tail: lin(0x4fae86),
    trailHot: lin(0xeafff2), trailTail: lin(0x35d894),
    spark: SPR.WISP, body: SPR.WISP, motes: SPR.DOT,
    light: 0x9ff0c8, lightI: 6, lightR: 10,
    grav: 1.0, drag: 1.6, turb: 1.1,
  },
  slay: {
    name: 'slay',
    hot: lin(0xffd7f4), core: lin(0xa03cd8), tail: lin(0x2a0640),
    trailHot: lin(0xf2b7ff), trailTail: lin(0x7a10d8),
    spark: SPR.GLOW, body: SPR.WISP, motes: SPR.EMBER,
    light: 0xb84cff, lightI: 11, lightR: 12,
    grav: 2.4, drag: 2.2, turb: 0.9,
  },
};
ELEMENTS.dark = ELEMENTS.slay;
ELEMENTS.thunder = ELEMENTS.lightning;
ELEMENTS.flame = ELEMENTS.fire;

/* ------------------------------------------------------------------ defs -- */
export const DEFS = {

  /* ---------------------------------------------------------- hit sparks -- */
  /** The bread-and-butter contact spark: 6-12 frames, hot core, radial streaks. */
  hit_slash: {
    layers: [
      // white-hot core flare — deliberately SMALL. Bloom does the rest, and a
      // big soft core plus bloom reads as fog instead of as a strike.
      // Measured against plate D at r2: the burst was visible for about three
      // frames and peaked at (255,242,211) with no pixel above 230 in all
      // three channels, so it never clipped to a hot core and bloom had
      // nothing to catch. The lifetimes were already in band -- `curve` was
      // the culprit, dumping most of the alpha in the first third -- so the
      // decay is eased and the core lifted rather than the life stretched far.
      { k: 'p', n: 1, sprite: SPR.STAR, dir: 'sphere', speed: 0, drag: 12,
        // The peak is NOT raisable from here. bright 4.1 -> 5.4, easing the
        // curve, and whitening this gradient all leave it at exactly
        // (255,242,211) with 0 px above 230 in all three channels: the ceiling
        // is the ACES shoulder and the scene exposure, downstream of any
        // particle def, so a hot clipped core is postfx/lighting work. What
        // IS fixed from here is persistence -- see the curve below.
        life: 0.15, size0: 0.26, size1: 0.74, bright: 5.4,
        col: 0xfffdf4, col2: 0xffd070, alpha: 1, alpha2: 0, curve: 1.45, fadeIn: 0.015,
        spin: [0, 6.28], spinRate: [-3, 3] },
      // the radial streak fan, in the plane facing the hit direction
      { k: 'p', n: [8, 12], sprite: SPR.STREAK, dir: 'disc', speed: [13, 27], drag: [9, 15],
        life: [0.13, 0.22], size0: [0.18, 0.34], size1: [0.04, 0.09], bright: 3.4,
        col: 0xfffbe4, col2: 0xff9c2c, alpha: 1, alpha2: 0, curve: 1.15,
        align: 3, stretch: 0.090, offset: 0.05 },
      // cinders that survive the flash and fall
      { k: 'p', n: [3, 6], sprite: SPR.GLOW, dir: 'hemi', speed: [4.5, 10], drag: [2.4, 4.0],
        life: [0.28, 0.58], size0: [0.05, 0.11], size1: [0.012, 0.03], bright: 2.1,
        col: 0xffe6a0, col2: 0xd8501a, alpha: 1, alpha2: 0, curve: 1.2, grav: -16,
        align: 1, stretch: 0.03 },
      // a brief expanding shell so the burst has volume, not just spokes
      { k: 'p', n: 1, sprite: SPR.RING, dir: 'along', speed: 0, drag: 20,
        life: 0.18, size0: 0.18, size1: 1.05, bright: 1.8,
        col: 0xfff2cc, col2: 0xff8c30, alpha: 0.45, alpha2: 0, curve: 1.55, fadeIn: 0.03 },
      { k: 'light', color: 0xffd79a, intensity: 8, radius: 6, life: 0.15 },
    ],
  },

  /** Secondary crowd contact: location read only, never another white core. */
  hit_slash_fill: {
    layers: [
      { k: 'p', n: [2, 4], sprite: SPR.STREAK, dir: 'disc', speed: [10, 22], drag: [10, 16],
        life: [0.08, 0.15], size0: [0.10, 0.20], size1: [0.02, 0.05], bright: 1.8,
        col: 0xfff0d0, col2: 0xd97828, alpha: 0.66, alpha2: 0, curve: 1.6,
        align: 3, stretch: 0.070 },
      { k: 'p', n: [1, 2], sprite: SPR.EMBER, dir: 'hemi', speed: [3, 7], drag: 3.0,
        life: [0.20, 0.38], size0: [0.035, 0.07], size1: 0.012, bright: 1.4,
        col: 0xffd080, col2: 0xb84818, alpha: 0.65, alpha2: 0, grav: -14 },
    ],
  },

  /** Blunt / crush: fewer spokes, a fat dusty shell, a lower colour. */
  hit_blunt: {
    layers: [
      { k: 'p', n: 1, sprite: SPR.STAR, dir: 'sphere', speed: 0, drag: 14,
        life: 0.13, size0: 0.32, size1: 0.90, bright: 3.0,
        col: 0xfff4dc, col2: 0xe9a24c, alpha: 1, alpha2: 0, curve: 2.0, spin: [0, 6.28] },
      { k: 'p', n: [5, 8], sprite: SPR.STREAK, dir: 'disc', speed: [7, 15], drag: [10, 16],
        life: [0.09, 0.16], size0: [0.20, 0.38], size1: [0.05, 0.11], bright: 2.2,
        col: 0xfff0d0, col2: 0xd07828, alpha: 1, alpha2: 0, curve: 1.6, align: 3, stretch: 0.075 },
      { k: 'p', pool: 'alpha', n: [3, 5], sprite: [SPR.PUFF, SPR.PUFF2], dir: 'sphere',
        speed: [2.0, 5.5], drag: [2.6, 3.8], life: [0.45, 0.85],
        size0: [0.30, 0.55], size1: [1.0, 1.7], bright: 1,
        col: 0x9b8b76, col2: 0x6d6153, alpha: 0.55, alpha2: 0, curve: 1.1,
        spin: [0, 6.28], spinRate: [-1.6, 1.6], grav: 1.0 },
      { k: 'light', color: 0xffc888, intensity: 7, radius: 6, life: 0.11 },
    ],
  },

  /** Secondary blunt contact: a few low-value flecks and dust, no lamp/core. */
  hit_blunt_fill: {
    layers: [
      { k: 'p', n: [2, 3], sprite: SPR.STREAK, dir: 'disc', speed: [6, 13], drag: [11, 17],
        life: [0.08, 0.14], size0: [0.12, 0.23], size1: [0.035, 0.07], bright: 1.45,
        col: 0xffe8c4, col2: 0xc56c24, alpha: 0.58, alpha2: 0, align: 3, stretch: 0.055 },
      { k: 'p', pool: 'alpha', n: [1, 2], sprite: SPR.PUFF2, dir: 'sphere', speed: [1.5, 3.5],
        drag: 3.4, life: [0.28, 0.48], size0: [0.16, 0.28], size1: [0.45, 0.75],
        col: 0x96836c, col2: 0x6d6153, alpha: 0.28, alpha2: 0 },
    ],
  },

  /** Critical / charge finish: bigger, gold, with lens spikes and a shell. */
  hit_crit: {
    layers: [
      { k: 'p', n: 1, sprite: SPR.RAYS, dir: 'along', speed: 0, drag: 20,
        life: 0.24, size0: 0.5, size1: 2.4, bright: 3.4,
        col: 0xfffaea, col2: 0xffb24a, alpha: 0.85, alpha2: 0, curve: 2.4, spinRate: [-2.2, 2.2] },
      { k: 'p', n: 1, sprite: SPR.STAR, dir: 'sphere', speed: 0, drag: 12,
        life: 0.20, size0: 0.55, size1: 1.9, bright: 7.0,
        col: 0xffffff, col2: 0xffc860, alpha: 1, alpha2: 0, curve: 2.1, spin: [0, 6.28] },
      { k: 'p', n: [22, 30], sprite: SPR.STREAK, dir: 'disc', speed: [20, 46], drag: [7, 12],
        life: [0.16, 0.32], size0: [0.36, 0.66], size1: [0.07, 0.16], bright: 4.0,
        col: 0xfffbe8, col2: 0xff8a20, alpha: 1, alpha2: 0, curve: 1.4, align: 3, stretch: 0.085 },
      { k: 'p', n: [18, 26], sprite: SPR.EMBER, dir: 'sphere', speed: [6, 18], drag: [1.8, 3.2],
        life: [0.5, 1.1], size0: [0.10, 0.18], size1: [0.02, 0.05], bright: 3.2,
        col: 0xffdc90, col2: 0xc84010, alpha: 1, alpha2: 0, grav: -18, align: 1, stretch: 0.028 },
      { k: 'p', n: 1, sprite: SPR.RING, dir: 'along', speed: 0, drag: 20,
        life: 0.28, size0: 0.35, size1: 3.0, bright: 2.4,
        col: 0xfff0d0, col2: 0xff7a20, alpha: 0.75, alpha2: 0, curve: 1.7 },
      { k: 'ring', radius: 3.4, life: 0.42, thick: 0.085, col: 0xffc766, alpha: 0.85 },
      { k: 'light', color: 0xffca80, intensity: 26, radius: 13, life: 0.24 },
    ],
  },

  /** Guard: a hard white flash on the shield plus a shower of cold sparks. */
  hit_block: {
    layers: [
      { k: 'p', n: 1, sprite: SPR.STAR, dir: 'sphere', speed: 0, drag: 16,
        life: 0.11, size0: 0.40, size1: 1.35, bright: 8.0,
        col: 0xffffff, col2: 0xd8ecff, alpha: 1, alpha2: 0, curve: 2.6, spin: [0, 6.28] },
      { k: 'p', n: [20, 28], sprite: SPR.STREAK, dir: 'cone', spread: 1.35, speed: [9, 24],
        drag: [8, 14], life: [0.22, 0.46], size0: [0.14, 0.28], size1: [0.03, 0.08],
        bright: 3.8, col: 0xffffff, col2: 0x9fc4ff, alpha: 1, alpha2: 0, curve: 1.3,
        align: 3, stretch: 0.075, grav: -24 },
      { k: 'p', n: 1, sprite: SPR.RING, dir: 'along', speed: 0, drag: 20,
        life: 0.15, size0: 0.28, size1: 1.7, bright: 3.0,
        col: 0xf0f8ff, col2: 0x7fb4ff, alpha: 0.8, alpha2: 0, curve: 2.0 },
      { k: 'light', color: 0xbfe0ff, intensity: 18, radius: 9, life: 0.12 },
    ],
  },

  /** Thrust / arrow: a tight forward cone rather than a plane. */
  hit_pierce: {
    layers: [
      { k: 'p', n: 1, sprite: SPR.STAR, dir: 'sphere', speed: 0, drag: 14,
        life: 0.11, size0: 0.28, size1: 0.85, bright: 4.2, col: 0xfffdf0, col2: 0xffcc70,
        alpha: 1, alpha2: 0, curve: 2.2 },
      { k: 'p', n: [10, 15], sprite: SPR.STREAK, dir: 'cone', spread: 0.42, speed: [16, 34],
        drag: [8, 13], life: [0.10, 0.20], size0: [0.20, 0.40], size1: [0.04, 0.10],
        bright: 3.4, col: 0xfffbe4, col2: 0xff9c2c, alpha: 1, alpha2: 0, align: 3, stretch: 0.085 },
      { k: 'light', color: 0xffd79a, intensity: 9, radius: 6, life: 0.11 },
    ],
  },

  hit_pierce_fill: {
    layers: [
      { k: 'p', n: [2, 4], sprite: SPR.STREAK, dir: 'cone', spread: 0.32,
        speed: [12, 25], drag: [9, 14], life: [0.07, 0.14],
        size0: [0.09, 0.18], size1: [0.02, 0.05], bright: 1.7,
        col: 0xffefd0, col2: 0xd77c2b, alpha: 0.62, alpha2: 0,
        align: 3, stretch: 0.065 },
    ],
  },

  /* ---------------------------------------------------------------- dust -- */
  /** A single footfall. Kept deliberately small — it fires constantly. */
  dust_step: {
    layers: [
      { k: 'p', pool: 'alpha', n: [2, 4], sprite: [SPR.PUFF, SPR.PUFF2], dir: 'up',
        spread: 1.15, speed: [0.5, 1.5], drag: [2.2, 3.2], life: [0.42, 0.75],
        size0: [0.16, 0.30], size1: [0.55, 0.95], offset: 0.12, yoff: 0.03,
        col: 0xa8977e, col2: 0x7b6d5c, alpha: 0.42, alpha2: 0, curve: 1.2,
        spin: [0, 6.28], spinRate: [-1.2, 1.2], grav: 0.4 },
    ],
  },

  /** Landing from a jump / a body hitting the floor: a flat outward ring. */
  dust_land: {
    layers: [
      { k: 'p', pool: 'alpha', n: [10, 16], sprite: [SPR.PUFF, SPR.PUFF2], dir: 'ground',
        speed: [3.5, 8.0], drag: [3.0, 4.4], life: [0.55, 1.0],
        size0: [0.30, 0.55], size1: [1.3, 2.2], offset: 0.25, yoff: 0.06,
        col: 0xb4a289, col2: 0x7b6d5c, alpha: 0.52, alpha2: 0, curve: 1.15,
        spin: [0, 6.28], spinRate: [-1.4, 1.4], grav: 1.2 },
      { k: 'p', pool: 'alpha', n: [3, 5], sprite: SPR.SMOKE, dir: 'up', spread: 0.8,
        speed: [1.0, 2.4], drag: 2.0, life: [0.9, 1.5], size0: [0.5, 0.8], size1: [1.8, 2.8],
        col: 0xa39280, col2: 0x82755f, alpha: 0.3, alpha2: 0, spinRate: [-0.7, 0.7] },
      { k: 'p', n: [4, 8], sprite: SPR.DEBRIS, dir: 'ground', speed: [3, 8], drag: 1.2,
        life: [0.5, 0.9], size0: [0.05, 0.11], size1: [0.04, 0.09], grav: -20,
        col: 0x8a7a63, col2: 0x6b5e4c, alpha: 1, alpha2: 1, bright: 0.9,
        spin: [0, 6.28], spinRate: [-9, 9] },
      { k: 'decal', cell: 6, radius: [0.7, 1.1], life: 22, alpha: 0.30, col: 0x6a5c48 },
    ],
  },

  /** Dirt torn up by a body sliding along the ground under knockback. */
  dust_slide: {
    layers: [
      { k: 'p', pool: 'alpha', n: [4, 7], sprite: [SPR.PUFF2, SPR.PUFF], dir: 'back',
        spread: 0.7, speed: [2.5, 6.0], drag: [2.6, 4.0], life: [0.4, 0.8],
        size0: [0.18, 0.34], size1: [0.8, 1.4], offset: 0.2, yoff: 0.05,
        col: 0xa8977e, col2: 0x77694f, alpha: 0.45, alpha2: 0, spinRate: [-1.5, 1.5], grav: 0.6 },
      { k: 'p', n: [3, 6], sprite: SPR.DEBRIS, dir: 'back', spread: 0.55, speed: [4, 10],
        drag: 1.0, life: [0.4, 0.8], size0: [0.04, 0.09], size1: [0.03, 0.07], grav: -20,
        col: 0x7f7059, col2: 0x60543f, alpha: 1, alpha2: 1, bright: 0.9, spinRate: [-11, 11] },
    ],
  },

  /* --------------------------------------------------------------- slash -- */
  /**
   * The crescent card a sweep leaves when there is no skeleton to sample —
   * used for every crowd unit's attack, and as a second read on the hero's.
   */
  slash_arc: {
    layers: [
      { k: 'p', n: 1, sprite: SPR.SLASH, dir: 'along', speed: 0, drag: 20,
        life: 0.16, size0: [1.7, 2.0], size1: [2.7, 3.1], bright: 4.0,
        col: 0xfffdf4, col2: 0xffb056, alpha: 1, alpha2: 0, curve: 2.2, fadeIn: 0.05 },
      { k: 'p', n: [5, 8], sprite: SPR.STREAK, dir: 'disc', speed: [5, 13], drag: 7,
        life: [0.14, 0.26], size0: [0.22, 0.44], size1: [0.05, 0.12], bright: 2.2,
        col: 0xfff4d8, col2: 0xff9c40, alpha: 0.85, alpha2: 0, align: 3, stretch: 0.08 },
    ],
  },

  /* ------------------------------------------------------------- elements -- */
  elem_fire: {
    layers: [
      { k: 'p', n: [14, 20], sprite: SPR.FLAME, dir: 'hemi', speed: [1.5, 5.0], drag: [1.6, 2.6],
        life: [0.45, 0.85], size0: [0.35, 0.7], size1: [0.9, 1.5], bright: 3.2, grav: 3.4,
        turb: 0.5, col: 0xffe9a8, col2: 0xb02a06, alpha: 1, alpha2: 0, curve: 1.4,
        spin: [-0.3, 0.3], spinRate: [-0.9, 0.9] },
      { k: 'p', n: [10, 16], sprite: SPR.EMBER, dir: 'up', spread: 0.9, speed: [2.5, 6],
        drag: [1.2, 2.0], life: [0.7, 1.5], size0: [0.07, 0.14], size1: [0.02, 0.05],
        bright: 3.6, grav: 1.8, turb: 0.8, col: 0xffcf6a, col2: 0xd03408, alpha: 1, alpha2: 0 },
      { k: 'p', pool: 'alpha', n: [4, 7], sprite: SPR.SMOKE, dir: 'up', spread: 0.6,
        speed: [1.2, 2.6], drag: 1.6, life: [1.0, 1.8], size0: [0.4, 0.7], size1: [1.6, 2.6],
        col: 0x4a4038, col2: 0x35302b, alpha: 0.30, alpha2: 0, spinRate: [-0.6, 0.6], grav: 0.9 },
      { k: 'light', color: 0xff7a22, intensity: 12, radius: 12, life: 0.5 },
    ],
  },
  elem_ice: {
    layers: [
      { k: 'p', n: [16, 24], sprite: SPR.SHARD, dir: 'sphere', speed: [3, 11], drag: [2.6, 4.2],
        life: [0.35, 0.8], size0: [0.16, 0.36], size1: [0.05, 0.14], bright: 3.0, grav: -12,
        col: 0xeafcff, col2: 0x2f7fc8, alpha: 1, alpha2: 0, spin: [0, 6.28], spinRate: [-7, 7] },
      { k: 'p', n: 1, sprite: SPR.RING, dir: 'along', speed: 0, drag: 20, life: 0.24,
        size0: 0.4, size1: 3.2, bright: 3.0, col: 0xf4feff, col2: 0x63b8f0, alpha: 0.9, alpha2: 0 },
      { k: 'p', pool: 'alpha', n: [6, 10], sprite: SPR.SMOKE, dir: 'ground', speed: [1.5, 4],
        drag: 3.0, life: [0.7, 1.3], size0: [0.3, 0.5], size1: [1.1, 1.8],
        col: 0xd8f0ff, col2: 0xa8ccdf, alpha: 0.34, alpha2: 0, spinRate: [-0.8, 0.8] },
      { k: 'ring', radius: 3.0, life: 0.5, thick: 0.09, col: 0x8fd8ff, alpha: 0.7 },
      { k: 'light', color: 0x7fd8ff, intensity: 9, radius: 11, life: 0.3 },
    ],
  },
  elem_lightning: {
    layers: [
      { k: 'p', n: [10, 15], sprite: SPR.BOLT, dir: 'sphere', speed: [6, 16], drag: [7, 11],
        life: [0.09, 0.18], size0: [0.5, 1.1], size1: [0.3, 0.7], bright: 5.0, turb: 1.6,
        col: 0xffffff, col2: 0x7a63ff, alpha: 1, alpha2: 0, curve: 2.2, align: 3, stretch: 0.02 },
      { k: 'p', n: [10, 16], sprite: SPR.GLOW, dir: 'sphere', speed: [8, 20], drag: [6, 10],
        life: [0.15, 0.35], size0: [0.10, 0.20], size1: [0.02, 0.06], bright: 4.0,
        col: 0xe6f0ff, col2: 0x5a46e0, alpha: 1, alpha2: 0, align: 3, stretch: 0.07 },
      { k: 'beam', n: [3, 5], style: 1, len: [2.2, 4.5], life: [0.09, 0.16], w0: 0.16, w1: 0.10,
        jitter: 0.22, bright: 4.5, col: 0xffffff, col2: 0x8f7bff, alpha: 1 },
      { k: 'light', color: 0xa9c8ff, intensity: 20, radius: 15, life: 0.14 },
      { k: 'impulse', kind: 'heavy', amount: 0.8 },
    ],
  },
  elem_wind: {
    layers: [
      { k: 'p', n: [14, 20], sprite: SPR.WISP, dir: 'disc', speed: [7, 16], drag: [1.8, 2.8],
        life: [0.35, 0.7], size0: [0.5, 1.0], size1: [0.2, 0.5], bright: 2.2, turb: 0.8,
        col: 0xeafff2, col2: 0x4fae86, alpha: 0.85, alpha2: 0, align: 3, stretch: 0.065 },
      { k: 'p', n: 2, sprite: SPR.RING, dir: 'along', speed: 0, drag: 20, life: [0.26, 0.36],
        size0: 0.5, size1: [3.0, 4.4], bright: 1.9, col: 0xf0fff6, col2: 0x74c8a2,
        alpha: 0.7, alpha2: 0, delay: [0, 0.07] },
      { k: 'light', color: 0x9ff0c8, intensity: 6, radius: 10, life: 0.24 },
    ],
  },
  elem_slay: {
    layers: [
      { k: 'p', n: [16, 24], sprite: SPR.WISP, dir: 'up', spread: 1.3, speed: [2, 7],
        drag: [1.6, 2.6], life: [0.5, 1.1], size0: [0.3, 0.7], size1: [0.7, 1.3],
        bright: 2.6, turb: 0.9, grav: 1.6, col: 0xe8a8ff, col2: 0x35085c,
        alpha: 0.9, alpha2: 0, spinRate: [-1.2, 1.2] },
      { k: 'p', n: [10, 16], sprite: SPR.EMBER, dir: 'up', spread: 0.7, speed: [3, 8],
        drag: 1.4, life: [0.6, 1.3], size0: [0.08, 0.16], size1: [0.02, 0.05], bright: 3.4,
        grav: 1.2, turb: 1.0, col: 0xf6c0ff, col2: 0x8020c8, alpha: 1, alpha2: 0 },
      { k: 'p', n: 1, sprite: SPR.RAYS, dir: 'along', speed: 0, drag: 20, life: 0.3,
        size0: 0.6, size1: 3.6, bright: 3.2, col: 0xffd7f4, col2: 0x7a10c0,
        alpha: 0.9, alpha2: 0, spinRate: [-1.6, 1.6] },
      { k: 'ring', radius: 3.4, life: 0.5, thick: 0.10, col: 0xb84cff, alpha: 0.8 },
      { k: 'light', color: 0xb84cff, intensity: 11, radius: 12, life: 0.3 },
    ],
  },

  /* --------------------------------------------------------------- musou -- */
  /** The gathering charge before the finisher: motes converge, ground lights. */
  musou_charge: {
    layers: [
      { k: 'p', n: [10, 14], sprite: SPR.EMBER, dir: 'up', spread: 1.5, speed: [1.5, 4],
        drag: [1.0, 1.8], life: [0.6, 1.1], size0: [0.10, 0.22], size1: [0.02, 0.06],
        bright: 4.0, grav: 5.0, turb: 0.6, offset: 1.1, yoff: 0.1,
        col: 0xfff0c0, col2: 0xff8a20, alpha: 1, alpha2: 0 },
      { k: 'p', n: [3, 5], sprite: SPR.FLAME, dir: 'up', spread: 0.35, speed: [2, 4.5],
        drag: 1.6, life: [0.5, 0.9], size0: [0.6, 1.0], size1: [1.4, 2.2], bright: 2.6,
        grav: 3.0, turb: 0.4, offset: 0.5, col: 0xffdca0, col2: 0xd04808, alpha: 0.8, alpha2: 0 },
      { k: 'glow', radius: 3.0, life: 0.5, col: 0xffb257, alpha: 0.55 },
      { k: 'light', color: 0xffb257, intensity: 9, radius: 10, life: 0.4 },
    ],
  },

  /** Grinder contact: enough direction to read the pulse, never a new release. */
  musou_hit: {
    priority: 2,
    layers: [
      { k: 'p', n: [6, 10], sprite: SPR.STREAK, dir: 'ground', speed: [8, 18],
        drag: [8, 14], life: [0.10, 0.18], size0: [0.12, 0.24], size1: [0.025, 0.065],
        bright: 2.1, yoff: 0.35, col: 0xfff1cf, col2: 0xe17124,
        alpha: 0.72, alpha2: 0, align: 3, stretch: 0.06 },
      { k: 'p', n: [3, 5], sprite: SPR.EMBER, dir: 'up', spread: 0.9,
        speed: [2.5, 6], drag: 2.2, life: [0.25, 0.48], size0: [0.045, 0.09],
        size1: 0.014, bright: 1.8, col: 0xffcc72, col2: 0xc44718,
        alpha: 0.75, alpha2: 0, grav: -9 },
    ],
  },

  /** One Musou release: authored silhouette, one ring, no post/shake ownership. */
  musou_burst: {
    priority: 2,
    layers: [
      // Release is deliberately LOW and centrifugal. Vertical hierarchy is
      // reserved for the authored finish, so the two events cannot alias.
      //
      // That constraint was being honoured and the release was still losing to
      // an ordinary hit. Measured with critic_musou_ab -- each def emitted at
      // the SAME world point, from the same camera, sampled at the same
      // particle age, so the only variable is the def -- the peak frame gave:
      //
      //             footprint   >200 px   >245 px (hot core)
      //   hit_crit      42071     28097       990
      //   musou_burst   31104      8974       109
      //   musou_finish  72437     32908       330
      //
      // The biggest thing the player can do covered less ground than a normal
      // critical, at a third of its bright area and a ninth of its core. The
      // finish was already the largest of the three, so the budget and the
      // vocabulary were never the problem -- the release simply had no origin
      // and too few, too dim spokes. Raised WITHIN the constraint: the new
      // core is round and brief (0.13 s) so it reads as the point the blast
      // came from, not as a sustained shape that could be mistaken for the
      // finisher, and every travelling layer still leaves on `dir: 'ground'`.
      { k: 'p', n: 1, sprite: SPR.STAR, dir: 'sphere', speed: 0, drag: 13,
        life: 0.13, size0: 0.58, size1: 2.05, bright: 6.4,
        col: 0xfff6e2, col2: 0xffb45a, alpha: 1, alpha2: 0, curve: 2.0, spin: [0, 6.28] },
      { k: 'p', n: [22, 28], sprite: SPR.STREAK, dir: 'ground', speed: [16, 34], drag: [6, 10],
        life: [0.18, 0.32], size0: [0.22, 0.44], size1: [0.03, 0.08], bright: 3.6, yoff: 0.18,
        col: 0xffedcd, col2: 0xd66a22, alpha: 1, alpha2: 0, align: 3, stretch: 0.070 },
      { k: 'p', n: [5, 7], sprite: SPR.WISP, dir: 'ground', speed: [5, 11], drag: [4, 7],
        life: [0.22, 0.40], size0: [0.20, 0.34], size1: [0.44, 0.78], bright: 1.9, yoff: 0.12,
        col: 0xffc878, col2: 0x9b4319, alpha: 0.52, alpha2: 0, align: 3, stretch: 0.045 },
      { k: 'p', n: [10, 14], sprite: SPR.EMBER, dir: 'up', spread: 1.15, speed: [2.5, 7.5],
        drag: [1.8, 2.8], life: [0.35, 0.72], size0: [0.05, 0.10], size1: [0.014, 0.030],
        bright: 2.9, grav: -4.0, turb: 0.45, offset: 0.75,
        col: 0xffc66d, col2: 0xaa3812, alpha: 1, alpha2: 0 },
      // An expanding shell, as the crit has, so the blast has volume and not
      // just spokes. Flat (`dir: 'along'` on the release's own up axis) keeps
      // it a ground-plane disc rather than a rising dome.
      { k: 'p', n: 1, sprite: SPR.RING, dir: 'along', speed: 0, drag: 18,
        life: 0.26, size0: 0.40, size1: 3.6, bright: 2.6,
        col: 0xfff0d0, col2: 0xff8c30, alpha: 0.72, alpha2: 0, curve: 1.6 },
      { k: 'p', pool: 'alpha', n: [4, 6], sprite: SPR.PUFF2, dir: 'ground', speed: [2, 5.5],
        drag: [3.2, 4.5], life: [0.42, 0.75], size0: [0.24, 0.40], size1: [0.85, 1.35], yoff: 0.08,
        col: 0x776a59, col2: 0x514a40, alpha: 0.30, alpha2: 0, spinRate: [-0.6, 0.6], grav: 0.6 },
      { k: 'ring', radius: 7.4, life: 0.50, thick: 0.095, col: 0xffb950, alpha: 0.78 },
      { k: 'light', color: 0xffad55, intensity: 30, radius: 15, life: 0.22 },
    ],
  },

  /** Musou finisher: one authored ring; gameplay owns post, hitstop and shake. */
  musou_finish: {
    priority: 2,
    layers: [
      // One ascending command stroke plus a directional three-cut fan. These
      // reuse the pooled beam draw and read by shape rather than white luma.
      { k: 'beam', n: 1, axis: 'vertical', style: 3, len: 5.2, life: 0.62,
        y0: -1.0, lean: 0.08, w0: 0.24, w1: 0.028, bright: 1.85,
        col: 0xffd77d, col2: 0xc84b14, alpha: 0.72 },
      { k: 'beam', n: 3, axis: 'fan', style: 3, len: [4.4, 5.6], life: [0.54, 0.66],
        y0: -0.72, spread: 1.20, rise: 0.90, w0: 0.18, w1: 0.022, bright: 1.65,
        col: 0xffbd50, col2: 0xb93b0d, alpha: 0.64 },
      { k: 'p', n: [10, 14], sprite: SPR.STREAK, dir: 'cone', spread: 0.34,
        speed: [13, 25], drag: [4.5, 7], life: [0.20, 0.38],
        size0: [0.10, 0.22], size1: [0.02, 0.055], bright: 1.65, yoff: -0.72,
        col: 0xffe7b2, col2: 0xd25b1d, alpha: 0.50, alpha2: 0, align: 3, stretch: 0.052 },
      { k: 'p', n: [8, 11], sprite: SPR.EMBER, dir: 'up', spread: 0.52, speed: [5, 12], drag: 2.4,
        life: [0.34, 0.64], size0: [0.045, 0.095], size1: [0.012, 0.028], bright: 2.0,
        col: 0xffc467, col2: 0xa9360f, alpha: 0.68, alpha2: 0, grav: -11 },
      { k: 'ring', radius: 5.8, life: 0.52, thick: 0.046, col: 0xffc267, alpha: 0.32 },
      { k: 'light', color: 0xffcb82, intensity: 8, radius: 12, life: 0.20 },
    ],
  },

  /* ---------------------------------------------------- move semantics -- */
  /** Basic swings already own a blade ribbon and body contacts. */
  move_contact_only: { layers: [] },

  /** One restrained directional accent for a connected heavy sweep. */
  move_slash_big: {
    layers: [
      { k: 'p', n: [4, 7], sprite: SPR.STREAK, dir: 'disc', speed: [8, 18], drag: [8, 13],
        life: [0.12, 0.22], size0: [0.13, 0.28], size1: [0.03, 0.07], bright: 1.75,
        col: 0xffefd0, col2: 0xd87928, alpha: 0.64, alpha2: 0,
        align: 3, stretch: 0.065, yoff: 0.2 },
    ],
  },

  move_slash_huge: {
    priority: 2,
    layers: [
      { k: 'p', n: 1, sprite: SPR.RAYS, dir: 'along', speed: 0, drag: 20, life: 0.24,
        size0: 0.30, size1: 1.9, bright: 1.8, col: 0xfff3dc, col2: 0xe58a36,
        alpha: 0.42, alpha2: 0, curve: 2.2 },
      { k: 'p', n: [7, 10], sprite: SPR.STREAK, dir: 'disc', speed: [10, 23], drag: [7, 12],
        life: [0.14, 0.28], size0: [0.17, 0.34], size1: [0.04, 0.09], bright: 2.0,
        col: 0xfff0d4, col2: 0xdc7928, alpha: 0.68, alpha2: 0,
        align: 3, stretch: 0.065, yoff: 0.2 },
    ],
  },

  /** Spin/launch semantics add direction, never another full-screen card. */
  move_slash_spin: {
    layers: [
      { k: 'p', n: [5, 8], sprite: SPR.STREAK, dir: 'ground', speed: [8, 19], drag: [7, 12],
        life: [0.14, 0.25], size0: [0.13, 0.27], size1: [0.03, 0.07], bright: 1.65,
        col: 0xffedcc, col2: 0xcf7025, alpha: 0.58, alpha2: 0,
        align: 3, stretch: 0.06, yoff: 0.25 },
    ],
  },

  move_launch_arc: {
    layers: [
      { k: 'p', n: [5, 8], sprite: SPR.STREAK, dir: 'up', spread: 0.48,
        speed: [8, 20], drag: [6, 10], life: [0.14, 0.27],
        size0: [0.14, 0.29], size1: [0.03, 0.07], bright: 1.8,
        col: 0xffefcf, col2: 0xd67828, alpha: 0.65, alpha2: 0,
        align: 3, stretch: 0.065 },
    ],
  },

  move_stun: {
    layers: [
      { k: 'p', n: [4, 7], sprite: SPR.BOLT, dir: 'sphere', speed: [5, 13], drag: [8, 13],
        life: [0.10, 0.20], size0: [0.20, 0.42], size1: [0.08, 0.18], bright: 1.9,
        col: 0xfff2cf, col2: 0xc97828, alpha: 0.60, alpha2: 0, align: 3 },
    ],
  },

  /** Combat owns this one ring. The animation marker below owns only dust. */
  move_ground_slam: {
    priority: 2,
    layers: [
      { k: 'p', n: [5, 8], sprite: SPR.DEBRIS, dir: 'ground', speed: [5, 12], drag: 1.6,
        life: [0.35, 0.68], size0: [0.045, 0.10], size1: [0.025, 0.07], grav: -18,
        col: 0x8c795f, col2: 0x5d5141, alpha: 0.72, alpha2: 0, bright: 0.8,
        spinRate: [-8, 8], yoff: -0.65 },
      { k: 'ring', radius: 4.2, life: 0.42, thick: 0.075, col: 0xe0b46d, alpha: 0.38 },
    ],
  },

  /** Whiff/environment ownership: dirt at blade impact, deliberately no ring. */
  ground_slam_marker: {
    layers: [
      { k: 'p', pool: 'alpha', n: [3, 5], sprite: [SPR.PUFF, SPR.PUFF2], dir: 'ground',
        speed: [2, 5], drag: 3.5, life: [0.38, 0.72], size0: [0.18, 0.34], size1: [0.65, 1.05],
        col: 0x9c8a72, col2: 0x6d6153, alpha: 0.26, alpha2: 0,
        spinRate: [-1.0, 1.0], yoff: 0.04 },
    ],
  },

  move_shock: {
    layers: [
      { k: 'p', n: [4, 6], sprite: SPR.STREAK, dir: 'ground', speed: [7, 15], drag: [8, 13],
        life: [0.10, 0.20], size0: [0.11, 0.22], size1: [0.025, 0.055], bright: 1.6,
        col: 0xffe7bd, col2: 0xcc6c22, alpha: 0.55, alpha2: 0, align: 3, stretch: 0.055 },
      { k: 'ring', radius: 3.2, life: 0.34, thick: 0.065, col: 0xe3ae62, alpha: 0.30 },
    ],
  },

  move_slam: {
    layers: [
      { k: 'p', n: [4, 6], sprite: SPR.DEBRIS, dir: 'ground', speed: [4, 10], drag: 1.8,
        life: [0.30, 0.58], size0: [0.04, 0.09], size1: [0.025, 0.06], grav: -17,
        col: 0x8d7a62, col2: 0x5d5141, alpha: 0.65, alpha2: 0, bright: 0.75, yoff: -0.65 },
      { k: 'ring', radius: 3.5, life: 0.38, thick: 0.07, col: 0xd9ad67, alpha: 0.32 },
    ],
  },

  /* ---------------------------------------------------------- misc combat -- */
  /** A body dying: a short burst of motes and a ground scuff. */
  death_puff: {
    layers: [
      { k: 'p', n: [3, 5], sprite: SPR.EMBER, dir: 'up', spread: 1.2, speed: [2, 5],
        drag: [1.4, 2.2], life: [0.5, 1.0], size0: [0.07, 0.15], size1: [0.02, 0.04],
        bright: 2.6, grav: 0.8, turb: 0.5, col: 0xffd8a0, col2: 0xb04010, alpha: 1, alpha2: 0 },
      { k: 'p', pool: 'alpha', n: [2, 4], sprite: [SPR.PUFF, SPR.PUFF2], dir: 'ground',
        speed: [1.5, 4], drag: 3.0, life: [0.5, 0.9], size0: [0.25, 0.45], size1: [0.9, 1.5],
        col: 0x9c8b74, col2: 0x6d6153, alpha: 0.45, alpha2: 0, spinRate: [-1.2, 1.2] },
    ],
  },

  /** A unit arriving: an upward wash of dust plus a faint ring. */
  spawn_puff: {
    layers: [
      { k: 'p', pool: 'alpha', n: [6, 10], sprite: [SPR.PUFF, SPR.PUFF2], dir: 'up',
        spread: 1.2, speed: [1.5, 3.5], drag: 2.6, life: [0.5, 0.9],
        size0: [0.2, 0.4], size1: [0.9, 1.5], offset: 0.35,
        col: 0xa8977e, col2: 0x7b6d5c, alpha: 0.42, alpha2: 0, spinRate: [-1.2, 1.2] },
      { k: 'ring', radius: 1.6, life: 0.35, thick: 0.16, col: 0xd8c9ac, alpha: 0.3 },
    ],
  },
};

/** Aliases combat/AI can use without knowing the internal names. */
DEFS.hit = DEFS.hit_slash;
DEFS.slash = DEFS.move_contact_only;
DEFS.block = DEFS.hit_block;
DEFS.crit = DEFS.hit_crit;
DEFS.blunt = DEFS.hit_blunt;
DEFS.pierce = DEFS.move_contact_only;
DEFS.slashBig = DEFS.move_slash_big;
DEFS.slashHuge = DEFS.move_slash_huge;
DEFS.slashSpin = DEFS.move_slash_spin;
DEFS.launchArc = DEFS.move_launch_arc;
DEFS.stunBurst = DEFS.move_stun;
DEFS.groundSlam = DEFS.move_ground_slam;
DEFS.shock = DEFS.move_shock;
DEFS.slam = DEFS.move_slam;
DEFS.musouHit = DEFS.musou_hit;
DEFS.musouFinish = DEFS.musou_finish;
DEFS.musouBurst = DEFS.musou_burst;

/* Markers authored in src/anim/clips.js that had no def, so `vfx.emit(name)`
 * dropped them silently (docs/REQUESTS.md, combat -> vfx): every launched death
 * played with no launch puff and landed with no dust, and a blocked blow made
 * no spark. `groundSlam` is split above: the animation marker owns dust, while
 * the connected combat semantic owns its single readable ring. */
DEFS.landDust = DEFS.dust_land;
DEFS.launch = DEFS.dust_land;
DEFS.guardSpark = DEFS.hit_block;
